import { useState, useEffect } from "react"
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { CallToAction } from "~/ui-rtk/api/types/paid-media.types"

export interface CreativeOverrides {
  destinationUrl?: string
  headline?: string
  description?: string
  primaryText?: string
  callToAction?: CallToAction
}

interface CreativeOverridesSectionProps {
  overrides: CreativeOverrides
  onOverridesChange: (overrides: CreativeOverrides) => void
  sourceDestinationUrl?: string
  sourceHeadline?: string
  sourceDescription?: string
  sourcePrimaryText?: string
  sourceCta?: CallToAction
  disabled?: boolean
  requiredFields?: string[]
}

// Available CTA options for Meta platform
const CTA_OPTIONS: { value: CallToAction; label: string }[] = [
  { value: CallToAction.LEARN_MORE, label: "Learn More" },
  { value: CallToAction.SHOP_NOW, label: "Shop Now" },
  { value: CallToAction.SIGN_UP, label: "Sign Up" },
  { value: CallToAction.BOOK_NOW, label: "Book Now" },
  { value: CallToAction.CONTACT_US, label: "Contact Us" },
  { value: CallToAction.DOWNLOAD, label: "Download" },
  { value: CallToAction.GET_OFFER, label: "Get Offer" },
  { value: CallToAction.GET_QUOTE, label: "Get Quote" },
  { value: CallToAction.SUBSCRIBE, label: "Subscribe" },
  { value: CallToAction.WATCH_MORE, label: "Watch More" },
  { value: CallToAction.APPLY_NOW, label: "Apply Now" },
  {
    value: CallToAction.VIEW_INSTAGRAM_PROFILE,
    label: "View Instagram Profile",
  },
]

const FIELD_LABELS: Record<string, string> = {
  destinationUrl: "Destination URL",
  headline: "Headline",
  description: "Description",
  primaryText: "Primary Text",
  callToAction: "Call to Action",
}

export default function CreativeOverridesSection({
  overrides,
  onOverridesChange,
  sourceDestinationUrl,
  sourceHeadline,
  sourceDescription,
  sourcePrimaryText,
  sourceCta,
  disabled = false,
  requiredFields = [],
}: CreativeOverridesSectionProps) {
  const [isExpanded, setIsExpanded] = useState(requiredFields.length > 0)

  const isRequired = (field: string) => requiredFields.includes(field)
  const hasRequiredFields = requiredFields.length > 0

  // Auto-expand when backend signals fields are required
  useEffect(() => {
    if (requiredFields.length > 0) {
      setIsExpanded(true)
    }
  }, [requiredFields])

  const handleFieldChange = (
    field: keyof CreativeOverrides,
    value: string | boolean | CallToAction | undefined,
  ) => {
    onOverridesChange({
      ...overrides,
      [field]: value,
    })
  }

  const hasOverrides =
    overrides.destinationUrl ||
    overrides.headline ||
    overrides.description ||
    overrides.primaryText ||
    overrides.callToAction

  const requiredFieldLabels = requiredFields
    .map(f => FIELD_LABELS[f] || f)
    .join(", ")

  return (
    <div className="px-6 pb-4">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-3 text-left"
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          <h6 className="text-gray-400 text-sm font-medium uppercase tracking-wide">
            Creative Overrides
          </h6>
          {hasOverrides && (
            <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
              Modified
            </span>
          )}
          {hasRequiredFields && (
            <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full">
              Action Required
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>

      <p className="text-xs text-gray-500 mb-3">
        Override creative fields when blasting to ad sets with different
        objectives. Leave blank to use source values.
      </p>

      {hasRequiredFields && (
        <p className="text-xs text-amber-400 mb-3">
          The following fields are required: {requiredFieldLabels}. Please fill
          them in before blasting again.
        </p>
      )}

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="space-y-4 pt-2 pb-4 border-b border-system-blue-600">
          {/* Destination URL */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Destination URL
              {isRequired("destinationUrl") && (
                <span className="text-red-400 ml-1">*</span>
              )}
            </label>
            <input
              type="url"
              value={overrides.destinationUrl ?? ""}
              onChange={e =>
                handleFieldChange("destinationUrl", e.target.value)
              }
              placeholder={sourceDestinationUrl || "https://example.com"}
              disabled={disabled}
              className={cn(
                "w-full px-3 py-2 bg-system-blue-700 border rounded-lg",
                "text-white placeholder-gray-500 text-sm",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                isRequired("destinationUrl") && !overrides.destinationUrl
                  ? "border-amber-500"
                  : "border-system-blue-600",
                disabled && "opacity-50 cursor-not-allowed",
              )}
            />
            {sourceDestinationUrl && (
              <p className="text-xs text-gray-500 mt-1">
                Source: {sourceDestinationUrl}
              </p>
            )}
          </div>

          {/* Headline */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Headline
              {isRequired("headline") && (
                <span className="text-red-400 ml-1">*</span>
              )}
            </label>
            <input
              type="text"
              value={overrides.headline ?? ""}
              onChange={e => handleFieldChange("headline", e.target.value)}
              placeholder={sourceHeadline || "Enter headline"}
              disabled={disabled}
              maxLength={40}
              className={cn(
                "w-full px-3 py-2 bg-system-blue-700 border rounded-lg",
                "text-white placeholder-gray-500 text-sm",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                isRequired("headline") && !overrides.headline
                  ? "border-amber-500"
                  : "border-system-blue-600",
                disabled && "opacity-50 cursor-not-allowed",
              )}
            />
            <p className="text-xs text-gray-500 mt-1">
              {overrides.headline?.length ?? 0}/40 characters
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Description
              {isRequired("description") && (
                <span className="text-red-400 ml-1">*</span>
              )}
            </label>
            <input
              type="text"
              value={overrides.description ?? ""}
              onChange={e => handleFieldChange("description", e.target.value)}
              placeholder={sourceDescription || "Enter description"}
              disabled={disabled}
              maxLength={125}
              className={cn(
                "w-full px-3 py-2 bg-system-blue-700 border rounded-lg",
                "text-white placeholder-gray-500 text-sm",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                isRequired("description") && !overrides.description
                  ? "border-amber-500"
                  : "border-system-blue-600",
                disabled && "opacity-50 cursor-not-allowed",
              )}
            />
            <p className="text-xs text-gray-500 mt-1">
              {overrides.description?.length ?? 0}/125 characters
            </p>
          </div>

          {/* Primary Text */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Primary Text
              {isRequired("primaryText") && (
                <span className="text-red-400 ml-1">*</span>
              )}
            </label>
            <textarea
              value={overrides.primaryText ?? ""}
              onChange={e => handleFieldChange("primaryText", e.target.value)}
              placeholder={sourcePrimaryText || "Enter primary text"}
              disabled={disabled}
              maxLength={500}
              rows={3}
              className={cn(
                "w-full px-3 py-2 bg-system-blue-700 border rounded-lg",
                "text-white placeholder-gray-500 text-sm resize-none",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                isRequired("primaryText") && !overrides.primaryText
                  ? "border-amber-500"
                  : "border-system-blue-600",
                disabled && "opacity-50 cursor-not-allowed",
              )}
            />
            <p className="text-xs text-gray-500 mt-1">
              {overrides.primaryText?.length ?? 0}/500 characters
            </p>
          </div>

          {/* Call to Action */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Call to Action
              {isRequired("callToAction") && (
                <span className="text-red-400 ml-1">*</span>
              )}
            </label>
            <select
              value={overrides.callToAction ?? ""}
              onChange={e =>
                handleFieldChange(
                  "callToAction",
                  e.target.value ? (e.target.value as CallToAction) : undefined,
                )
              }
              disabled={disabled}
              className={cn(
                "w-full px-3 py-2 bg-system-blue-700 border rounded-lg",
                "text-white text-sm",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                isRequired("callToAction") && !overrides.callToAction
                  ? "border-amber-500"
                  : "border-system-blue-600",
                disabled && "opacity-50 cursor-not-allowed",
              )}
            >
              <option value="">
                {sourceCta ? `Use source (${sourceCta})` : "Select CTA"}
              </option>
              {CTA_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {sourceCta && (
              <p className="text-xs text-gray-500 mt-1">
                Source CTA: {sourceCta}
              </p>
            )}
          </div>

          {/* Clear Overrides Button */}
          {hasOverrides && (
            <button
              onClick={() =>
                onOverridesChange({
                  destinationUrl: undefined,
                  headline: undefined,
                  description: undefined,
                  primaryText: undefined,
                  callToAction: undefined,
                })
              }
              disabled={disabled}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Clear all overrides
            </button>
          )}
        </div>
      )}
    </div>
  )
}
